import { useEffect } from "react";
import Delta from "quill-delta";
import { formatCurrency } from "utils";
import { useSelector } from "react-redux";
import { useFormContext, useWatch } from "react-hook-form";
import { deskingSelectors } from "../model";
import { BuilderViewType, FinanceProgramFormValues } from "../types";

export function useFinanceOnlyFPDGenerator() {
  const currentTerm = useSelector(deskingSelectors.getSelectedFinanceOptionTerm);
  const financePrising = useSelector(deskingSelectors.financePrising);
  const menuBuilderViewType = useSelector(deskingSelectors.menuBuilderViewType);

  const { control, setValue } = useFormContext<FinanceProgramFormValues>();
  const [programType, daysToFirstPayment, fees] = useWatch({
    control,
    name: ["financeQuote.programType", "financeQuote.daysToFirstPayment", "financeQuote.fee"],
  });

  useEffect(() => {
    const isNeedGenerate = financePrising[currentTerm] && menuBuilderViewType === BuilderViewType.Finance;

    if (isNeedGenerate) {
      const generateFPD = () => {
        const { term, amountDueToday, rate, customerRate } = financePrising[currentTerm];

        const quillDeltaFPD = new Delta()
          .insert(programType, { bold: true })
          .insert(" | ")
          .insert(`${term} Months`)
          .insert(" | ")
          .insert(`${customerRate || rate}%`, { italic: true })
          .insert(" | ")
          .insert(`Deferred Payments: ${daysToFirstPayment ?? 0} Days to 1st Month's payment`)
          .insert("\n")
          .insert("Amount due in advance: ", { bold: true })
          .insert(formatCurrency(amountDueToday, true))
          .insert("\n");

        if (fees.length) {
          quillDeltaFPD.insert("Fees", { bold: true }).insert("\n");

          fees.forEach(({ identifier, fee }) => {
            quillDeltaFPD.insert(`${identifier}: `).insert(formatCurrency(fee, true)).insert("\n", { list: "bullet" });
          });
        }

        return quillDeltaFPD as any;
      };

      const fpd = generateFPD();
      setValue("financeProgramDescription", fpd);
    }
  }, [fees, currentTerm, financePrising, setValue, programType, daysToFirstPayment, menuBuilderViewType]);
}
